import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, Grid, Paper, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useVForm } from 'shared/forms';
import { FerramentasDeDetalhe, InputError, ConfirmacaoModal} from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { OrganizationsService } from 'shared/services/organizations/organizationsService';
import { ICreateOrganization, InicitalCreateOrganization } from 'shared/types';
import { t } from 'i18next';
import { useHasPermissionAcao } from 'shared/hooks';


const validationSchema = yup.object({
  CodigoOrganizacao: yup.number().required().moreThan(0),
  OrganizacaoSigla: yup.string().required(),
  OrganizacaoNome: yup.string().required(),
});

export const DetalheDeOrganizacao: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  
  const hasPermitionCreate = useHasPermissionAcao('organizacao','CRE');
  const hasPermitionDelete = useHasPermissionAcao('organizacao','DEL');
  const hasPermitionUpdate = useHasPermissionAcao('organizacao','UPD');

  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [messageConfirmacao, setMessageConfirmacao] = useState(''); 

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      OrganizationsService.getById(id)
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/organizacao/listagem');
          } else {
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateOrganization);  
    }

  }, [id]);

  // effect runs when user state is updated
  //useEffect(() => {
  //  reset form with user data
  //  reset(detalheWorkflow);
  //}, [detalheWorkflow]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.organizationSeletion.exclusion') + id + '?');
    setOpenConfirmacao(true);
    console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    OrganizationsService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'error',
          });
          navigate('/organizacao/listagem');
        }
      })
      .catch(reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateOrganization) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      OrganizationsService
        .create(data)
        .then((result) => { 
          setIsLoading(false);

          if (result instanceof Error) {  
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/organizacao/listagem');
            } else {
              navigate(`/organizacao/detalhe/${result}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      OrganizationsService
        .updateById(id, data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/organizacao/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'),
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };



  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? t('pages.organizationSeletion.newOrg') : hasPermitionUpdate?t('pages.organizationSeletion.changeOrg'):t('pages.organizationSeletion.viewOrg')}
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDeOrganizacao'
          textoBotaoNovo={t('components.actions.novo')}

          mostrarBotaoNovo={id !== 'novo' && hasPermitionCreate}
          mostrarBotaoApagar={id !== 'novo' && hasPermitionDelete}
          mostrarBotaoSalvar={id == 'novo' || hasPermitionUpdate}

          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/organizacao/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/organizacao/detalhe/novo')}
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDeOrganizacao' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.codigoOrganizacao?.type ? true : false}
                      fullWidth
                      id='CodigoOrganizacao'
                      type='text'
                      label={`${t('pages.organizationSeletion.codigoOrganizacao')} *`}
                      {...register('CodigoOrganizacao')}
                      disabled={isSubmitting || isLoading || id !== 'novo'}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.codigoOrganizacao?.type && <InputError type={errors.codigoOrganizacao.type} field='CodigoOrganizacao' />}
                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.organizacaoNome?.type ? true : false}
                      fullWidth
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      id='OrganizacaoNome'
                      type='text'
                      label={`${t('pages.organizationSeletion.organizacaoNome')} *`}
                      {...register('OrganizacaoNome')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.organizacaoNome?.type && <InputError type={errors.organizacaoNome.type} field='OrganizacaoNome' />}
                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.organizacaoSigla?.type ? true : false}
                      fullWidth
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      id='OrganizacaoSigla'
                      type='text'
                      label={`${t('pages.organizationSeletion.organizacaoSigla')} *`}
                      {...register('OrganizacaoSigla')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                      inputProps={{maxLength:3}}
                    />
                    {errors?.organizacaoSigla?.type && <InputError type={errors.organizacaoSigla.type} field='OrganizacaoSigla' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}
    </LayoutBaseDePagina >
  );
};
